import { useState } from "react";
import {
  hasElementFullscreen,
  isIOS,
  isPhoneScale,
} from "@/modules/orientationManager";
import { isPixiePortraitLocked } from "@/modules/pixieWorkspace";
import { settleAfterRotate } from "@/modules/rotateSettle";

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: HTMLElement["requestFullscreen"];
  webkitRequestFullScreen?: HTMLElement["requestFullscreen"];
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: Document["exitFullscreen"];
};

// The device rotation angle, used by isLandscapeNow. Prefer the deprecated
// `window.orientation`: on iOS `screen.orientation` can report a STALE value
// right after a rotate, whereas `window.orientation` reflects the real rotation.
// Fall back to `screen.orientation.angle` on platforms that dropped
// window.orientation (Android Chrome, desktop).
function readAngle() {
  if (typeof window !== "undefined" && typeof window.orientation === "number") {
    return window.orientation;
  }
  const so = (typeof screen !== "undefined" && screen.orientation) || null;
  if (so && typeof so.angle === "number") {
    return so.angle;
  }
  return null;
}

function isLandscapeNow() {
  const angle = readAngle();
  if (angle != null) {
    return angle === 90 || angle === -90 || angle === 270;
  }
  // angle is null here (window.orientation isn't a number and screen.orientation
  // has no angle), so fall back to type / media query.
  const so = (typeof screen !== "undefined" && screen.orientation) || null;
  if (so && typeof so.type === "string") {
    return so.type.indexOf("landscape") === 0;
  }
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(orientation: landscape)").matches;
  }
  return false;
}

// Expose which side the notch / Dynamic Island is on as data-pix-notch
// on <html>, so landscape.css insets ONLY that side's overlay (the other
// lane keeps its tight 12px). 90 → notch LEFT; -90 / 270 → notch RIGHT. A direct
// 180° landscape flip isn't reported by iOS (see app/css/landscape.css limitation), so
// the side updates reliably only when the rotation passes through portrait.
//
// NOTE: this writes to the page-global <html>, which assumes ONE Experience per
// document (the current embed model — multiple players share a page only behind
// separate iframes). If a host ever mounts two Experiences in the same document,
// the last rotate/mount would win the notch side for both; scope to a per-player
// ancestor (data-attr on the container) if that model ever changes.
function updateNotchSide() {
  if (typeof document === "undefined") {
    return;
  }
  const angle = readAngle();
  let side = "";
  if (angle === 90) {
    side = "left";
  } else if (angle === -90 || angle === 270) {
    side = "right";
  }
  document.documentElement.setAttribute("data-pix-notch", side);
}

function currentScreenOrientation() {
  return (typeof screen !== "undefined" && screen.orientation) || null;
}

function createAutoFullscreen() {
  const fs = {
    // The rendered .experience-container node (both layout branches attach this
    // ref), so the auto-fullscreen handler scopes to THIS Experience's container
    // rather than a document-wide querySelector.
    containerEl: null as HTMLDivElement | null,
    // Cancels the in-flight notch settle loop (see scheduleNotchUpdate).
    cancelNotchSettle: null as (() => void) | null,
    setContainerEl(el: HTMLDivElement | null) {
      fs.containerEl = el;
    },
    addOrientationListener() {
      const so = currentScreenOrientation();
      if (so && typeof so.addEventListener === "function") {
        so.addEventListener("change", onOrientationChangeFs);
      } else if (typeof window !== "undefined") {
        window.addEventListener("orientationchange", onOrientationChangeFs);
      }
    },
    removeOrientationListener() {
      const so = currentScreenOrientation();
      if (so && typeof so.removeEventListener === "function") {
        so.removeEventListener("change", onOrientationChangeFs);
      } else if (typeof window !== "undefined") {
        window.removeEventListener("orientationchange", onOrientationChangeFs);
      }
    },
    scheduleNotchUpdate,
    clearNotch() {
      if (fs.cancelNotchSettle) {
        fs.cancelNotchSettle();
        fs.cancelNotchSettle = null;
      }
      if (typeof document !== "undefined") {
        document.documentElement.removeAttribute("data-pix-notch");
      }
    },
  };

  // On iOS, window.orientation / screen.orientation can still report the
  // PREVIOUS orientation at the instant the change event fires (the same lag that
  // leaves the viewport size stale — see sizeManager), so a single read sets the
  // wrong notch side and never corrects, leaving the notch-side overlay (e.g. the
  // shop tabs) uninset. settleAfterRotate re-reads the angle every animation frame
  // until it settles — as fast as possible — and updateNotchSide only writes the
  // <html> attribute, so the repeats are cheap idempotent no-ops. (A direct 180°
  // flip is never reported by iOS; a portrait↔landscape change DOES settle, late.)
  function scheduleNotchUpdate() {
    if (fs.cancelNotchSettle) {
      fs.cancelNotchSettle();
    }
    fs.cancelNotchSettle = settleAfterRotate(updateNotchSide);
  }

  // MUST stay synchronous: the gesture-free fullscreen grant depends on the call
  // being inside the orientation-change event's call stack (no await / setTimeout /
  // React re-render deferral before requestFullscreen). iOS is excluded (no element
  // Fullscreen API — it uses the pseudo-fullscreen button). If the browser rejects
  // (older Chrome / Firefox / Safari), we keep today's immersive landscape layout.
  function onOrientationChangeFs() {
    // Keep the notch side in sync on every rotation (all devices), before the
    // Android-only fullscreen gate below.
    scheduleNotchUpdate();
    if (
      typeof navigator === "undefined" ||
      isIOS() ||
      (navigator.maxTouchPoints || 0) === 0 ||
      !isPhoneScale() ||
      !hasElementFullscreen()
    ) {
      return;
    }
    // Scope to THIS Experience's container (ref), not a document-wide query.
    const container = (fs.containerEl ||
      (typeof document !== "undefined"
        ? document.querySelector(".experience-container")
        : null)) as FullscreenElement | null;
    if (!container) {
      return;
    }

    if (isLandscapeNow()) {
      const doc = document as FullscreenDocument;
      const fsEl = doc.fullscreenElement || doc.webkitFullscreenElement;
      if (fsEl) {
        return; // already fullscreen
      }
      const req =
        container.requestFullscreen ||
        container.webkitRequestFullscreen ||
        container.webkitRequestFullScreen;
      if (!req) {
        return;
      }
      // Flag the auto path so VideoJs._onFullscreenChange skips the landscape
      // orientation-lock — locking would block the rotate back to portrait that is
      // the exit trigger. Cleared on rejection, and on the real exit (VideoJs).
      window.__pixAutoFs = true;
      try {
        const pr = req.call(container, { navigationUI: "hide" });
        if (pr && pr.then) {
          pr.then(
            () => {
              // Fast double-rotate: if we rotated back to portrait while the
              // request was still in flight, exit the fullscreen we just entered
              // rather than leaving it stuck (and mislocked) in portrait.
              if (!isLandscapeNow()) {
                exitAutoFs();
              }
            },
            () => {
              window.__pixAutoFs = false;
            },
          );
        }
      } catch {
        window.__pixAutoFs = false;
      }
    } else if (!isPixiePortraitLocked()) {
      // Rotated back to portrait — exit our auto-entered fullscreen. Do NOT clear
      // __pixAutoFs here: an enter requested above may still be in flight (fsEl not
      // set yet). VideoJs._onFullscreenChange clears the flag on the real exit, and
      // the request's fulfillment handler above exits if it resolves after this.
      exitAutoFs();
    }
  }

  function exitAutoFs() {
    if (typeof document === "undefined") {
      return;
    }
    // Only auto-exit the fullscreen WE auto-entered. Without this
    // gate, a MANUALLY-entered fullscreen would also be closed on rotate-back when
    // the manual path's screen.orientation.lock('landscape') was denied — common in
    // the cross-origin iframe embed without a Permissions-Policy grant — so the
    // device could physically rotate back and reach this handler. That would break
    // "manual fullscreen unaffected". __pixAutoFs is only set on the auto path.
    if (typeof window === "undefined" || !window.__pixAutoFs) {
      return;
    }
    const doc = document as FullscreenDocument;
    const fsEl = doc.fullscreenElement || doc.webkitFullscreenElement;
    // Only exit a fullscreen we opened (our container) — never someone else's.
    if (!fsEl || (fs.containerEl && fsEl !== fs.containerEl)) {
      return;
    }
    const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
    if (exit) {
      try {
        const pe = exit.call(document);
        if (pe && pe.catch) {
          pe.catch(() => {
            /* ignore the rejection */
          });
        }
      } catch {
        /* exit can reject if not in fullscreen — ignore */
      }
    }
  }

  return fs;
}

/**
 * Auto-fullscreen on rotate to landscape (Android) and the notch side on <html>.
 * One instance per mount: the orientation handler keeps one identity, so the
 * remove in Experience's unmount gets the function the mount added.
 */
export function useAutoFullscreen() {
  const [fs] = useState(createAutoFullscreen);
  return fs;
}
